import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const COLLAPSED_STORAGE_KEY = 'cart-drawer-upsell-collapsed';
const COLLAPSED_HTML_CLASS = 'cart-drawer-upsell-collapsed';

/**
 * Cart drawer upsell add-to-cart + desktop nav.
 *
 * Script must load with the cart drawer shell (not only when upsell HTML exists):
 * Section morph uses DOMParser and does not execute <script> tags, so empty→filled
 * carts previously left <cart-drawer-upsell> unupgraded and buttons dead.
 */
class CartDrawerUpsell extends HTMLElement {
  /** @type {WeakSet<HTMLElement>} */
  #inflight = new WeakSet();

  /** @type {WeakMap<HTMLElement, { x: number, y: number, id: number }>} */
  #touchStarts = new WeakMap();

  /** @type {WeakSet<HTMLElement>} */
  #handledByTouch = new WeakSet();

  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLButtonElement | null} */
  #prevBtn = null;

  /** @type {HTMLButtonElement | null} */
  #nextBtn = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number[]} */
  #syncTimers = [];

  connectedCallback() {
    this.#bindElements();

    this.addEventListener('touchstart', this.#onTouchStart, { passive: true, capture: true });
    this.addEventListener('touchend', this.#onTouchEnd, { passive: false, capture: true });
    this.addEventListener('touchcancel', this.#onTouchCancel, { passive: true, capture: true });
    this.addEventListener('click', this.#onClick, true);

    this.#track?.addEventListener('scroll', this.#syncNav, { passive: true });
    window.addEventListener('resize', this.#syncNav);
    document.addEventListener('theme-drawer:open', this.#onDrawerOpen);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#bindElements();
        this.#syncNav();
      });
      if (this.#track) this.#resizeObserver.observe(this.#track);
    }

    this.#shuffleItems();
    this.#restoreCollapsedState();
    this.#syncPromoHeadingFromCart();
    this.#scheduleSyncNav();
  }

  disconnectedCallback() {
    this.removeEventListener('touchstart', this.#onTouchStart, true);
    this.removeEventListener('touchend', this.#onTouchEnd, true);
    this.removeEventListener('touchcancel', this.#onTouchCancel, true);
    this.removeEventListener('click', this.#onClick, true);

    this.#track?.removeEventListener('scroll', this.#syncNav);
    window.removeEventListener('resize', this.#syncNav);
    document.removeEventListener('theme-drawer:open', this.#onDrawerOpen);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#clearSyncTimers();
  }

  #clearSyncTimers() {
    for (const id of this.#syncTimers) window.clearTimeout(id);
    this.#syncTimers = [];
  }

  /** Re-measure nav after layout / drawer animation settles. */
  #scheduleSyncNav() {
    this.#clearSyncTimers();
    this.#syncNav();
    requestAnimationFrame(() => {
      this.#syncNav();
      requestAnimationFrame(() => this.#syncNav());
    });
    for (const delay of [50, 150, 350]) {
      this.#syncTimers.push(window.setTimeout(() => this.#syncNav(), delay));
    }
  }

  #bindElements() {
    const nextTrack = this.querySelector('[data-upsell-track]');
    if (nextTrack !== this.#track) {
      this.#track?.removeEventListener('scroll', this.#syncNav);
      this.#track = nextTrack instanceof HTMLElement ? nextTrack : null;
      this.#track?.addEventListener('scroll', this.#syncNav, { passive: true });
      if (this.#resizeObserver && this.#track) {
        this.#resizeObserver.disconnect();
        this.#resizeObserver.observe(this.#track);
      }
    }

    const prev = this.querySelector('[data-upsell-prev]');
    const next = this.querySelector('[data-upsell-next]');
    this.#prevBtn = prev instanceof HTMLButtonElement ? prev : null;
    this.#nextBtn = next instanceof HTMLButtonElement ? next : null;
  }

  #onDrawerOpen = (event) => {
    const drawer = event.target;
    if (!(drawer instanceof HTMLElement) || drawer.id !== 'cart-drawer') return;
    this.#bindElements();
    this.#shuffleItems();
    this.#restoreCollapsedState();
    this.#syncPromoHeadingFromCart();
    this.#scheduleSyncNav();
  };

  /**
   * Cart section morph wipes element classes — keep collapse on <html> + re-apply.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    const reapply = () => {
      this.#bindElements();
      this.#restoreCollapsedState();
      this.#syncPromoHeadingFromCart();
      this.#scheduleSyncNav();
    };

    if (event?.promise) {
      event.promise.then(reapply).catch(reapply);
    } else {
      window.setTimeout(reapply, 50);
    }
  };

  /**
   * Eligible cart total in cents — skip threshold gift lines.
   * @param {{ items?: Array<{ final_line_price?: number, line_price?: number, properties?: Record<string, string> }> } | null | undefined} cart
   */
  #eligibleTotalCents(cart) {
    const items = cart?.items;
    if (!Array.isArray(items)) return 0;

    return items.reduce((sum, item) => {
      const props = item?.properties || {};
      if (props._threshold_gift != null && props._threshold_gift !== '') return sum;
      const line = Number(item.final_line_price ?? item.line_price ?? 0);
      return sum + (Number.isFinite(line) ? line : 0);
    }, 0);
  }

  async #syncPromoHeadingFromCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`);
      if (!response.ok) return;
      const cart = await response.json();
      this.#syncPromoHeading(this.#eligibleTotalCents(cart));
    } catch {
      /* ignore */
    }
  }

  /**
   * Swap heading / description by cart discount stages.
   * @param {number} totalCents
   */
  #syncPromoHeading(totalCents) {
    const heading = this.querySelector('[data-upsell-heading]');
    if (!(heading instanceof HTMLElement)) return;

    const s1 = Number.parseInt(this.dataset.upsellStage1Amount || '0', 10) || 0;
    const s2 = Number.parseInt(this.dataset.upsellStage2Amount || '0', 10) || 0;
    const s3 = Number.parseInt(this.dataset.upsellStage3Amount || '0', 10) || 0;
    const t1 = this.dataset.upsellStage1Title || '';
    const t2 = this.dataset.upsellStage2Title || '';
    const t3 = this.dataset.upsellStage3Title || '';
    const fallback = this.dataset.upsellDefaultTitle || heading.textContent || '';

    let title = fallback;
    let promo = false;

    if (s3 > 0 && totalCents >= s3 && t3) {
      title = t3;
      promo = true;
    } else if (s2 > 0 && totalCents >= s2 && t2) {
      title = t2;
      promo = true;
    } else if (s1 > 0 && totalCents >= s1 && t1) {
      title = t1;
      promo = true;
    }

    heading.textContent = title;
    heading.classList.toggle('cart-drawer-upsell__heading--promo', promo);
    this.classList.toggle('cart-drawer-upsell--promo', promo);

    const description = this.querySelector('[data-upsell-description]');
    if (description instanceof HTMLElement) {
      description.hidden = promo;
    }
  }

  /**
   * Fisher–Yates shuffle: random order each open, show only limit items.
   */
  #shuffleItems() {
    this.#bindElements();
    const track = this.#track;
    if (!(track instanceof HTMLElement)) return;

    const items = Array.from(track.querySelectorAll('[data-upsell-item], .cart-drawer-upsell__item'));
    if (items.length < 2) {
      items.forEach((item) => {
        item.hidden = false;
      });
      return;
    }

    const limit = Math.max(1, Number.parseInt(this.dataset.upsellLimit || '8', 10) || 8);

    for (let i = items.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = items[i];
      items[i] = items[j];
      items[j] = tmp;
    }

    items.forEach((item, index) => {
      track.appendChild(item);
      item.hidden = index >= limit;
    });

    track.scrollLeft = 0;
  }

  /**
   * @param {number} direction
   */
  #scrollBy(direction) {
    this.#bindElements();
    const track = this.#track;
    if (!(track instanceof HTMLElement)) return;

    const firstItem =
      track.querySelector('.cart-drawer-upsell__item:not([hidden])') ||
      track.querySelector('.cart-drawer-upsell__item');
    const itemWidth =
      firstItem instanceof HTMLElement && firstItem.getBoundingClientRect().width > 0
        ? firstItem.getBoundingClientRect().width
        : 268;
    const styles = window.getComputedStyle(track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const amount = itemWidth + gap;

    track.scrollBy({ left: direction * amount, behavior: 'smooth' });
    this.#scheduleSyncNav();
  }

  #syncNav = () => {
    this.#bindElements();
    const track = this.#track;
    if (!(track instanceof HTMLElement)) return;

    // Drawer may be closed / mid-animation — keep controls enabled until real size exists
    if (track.clientWidth < 8) {
      this.#setNavDisabled(this.#prevBtn, false);
      this.#setNavDisabled(this.#nextBtn, false);
      return;
    }

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const atStart = track.scrollLeft <= 2;
    const atEnd = track.scrollLeft >= maxScroll - 2;
    const canScroll = maxScroll > 4;

    this.#setNavDisabled(this.#prevBtn, !canScroll || atStart);
    this.#setNavDisabled(this.#nextBtn, !canScroll || atEnd);
  };

  /**
   * @param {HTMLButtonElement | null} btn
   * @param {boolean} disabled
   */
  #setNavDisabled(btn, disabled) {
    if (!btn) return;
    btn.disabled = disabled;
    btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  }

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  #buttonFrom(target) {
    if (!(target instanceof Element)) return null;
    const button = target.closest('[data-upsell-add]');
    return button instanceof HTMLElement && this.contains(button) ? button : null;
  }

  /**
   * @param {TouchEvent} event
   */
  #onTouchStart = (event) => {
    const button = this.#buttonFrom(event.target);
    if (!button || button.hasAttribute('disabled') || button.disabled) return;

    const touch = event.changedTouches[0];
    if (!touch) return;

    this.#touchStarts.set(button, {
      x: touch.clientX,
      y: touch.clientY,
      id: touch.identifier,
    });
  };

  /**
   * @param {TouchEvent} event
   */
  #onTouchCancel = (event) => {
    const button = this.#buttonFrom(event.target);
    if (button) this.#touchStarts.delete(button);
  };

  /**
   * @param {TouchEvent} event
   */
  #onTouchEnd = (event) => {
    const button = this.#buttonFrom(event.target);
    if (!button) return;

    const start = this.#touchStarts.get(button);
    this.#touchStarts.delete(button);
    if (!start || button.hasAttribute('disabled') || button.disabled || this.#inflight.has(button)) return;

    const touch = Array.from(event.changedTouches).find((t) => t.identifier === start.id) || event.changedTouches[0];
    if (!touch) return;

    const dx = Math.abs(touch.clientX - start.x);
    const dy = Math.abs(touch.clientY - start.y);
    if (dx > 12 || dy > 12) return;

    event.preventDefault();
    event.stopPropagation();

    this.#handledByTouch.add(button);
    this.#addVariant(button);

    window.setTimeout(() => this.#handledByTouch.delete(button), 500);
  };

  /**
   * Desktop / keyboard / Android synthesized click.
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const toggle = event.target.closest('[data-upsell-toggle]');
    if (toggle instanceof HTMLElement && this.contains(toggle)) {
      event.preventDefault();
      event.stopPropagation();
      this.#togglePanel(toggle);
      return;
    }

    const nav = event.target.closest('[data-upsell-prev], [data-upsell-next]');
    if (nav instanceof HTMLElement && this.contains(nav)) {
      event.preventDefault();
      event.stopPropagation();
      if (nav instanceof HTMLButtonElement && nav.disabled) {
        this.#scheduleSyncNav();
        return;
      }
      this.#scrollBy(nav.hasAttribute('data-upsell-prev') ? -1 : 1);
      return;
    }

    const button = this.#buttonFrom(event.target);
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    if (this.#handledByTouch.has(button)) return;
    if (button.hasAttribute('disabled') || /** @type {any} */ (button).disabled || this.#inflight.has(button)) {
      return;
    }

    this.#addVariant(button);
  };

  /**
   * @param {HTMLElement} toggle
   */
  #togglePanel(toggle) {
    const collapsed = !this.#isCollapsed();
    this.#setCollapsed(collapsed, toggle);

    if (!collapsed) {
      this.#scheduleSyncNav();
    }
  }

  #isCollapsed() {
    return (
      document.documentElement.classList.contains(COLLAPSED_HTML_CLASS) ||
      this.classList.contains('is-collapsed')
    );
  }

  /**
   * @param {boolean} collapsed
   * @param {HTMLElement | null} [toggle]
   */
  #setCollapsed(collapsed, toggle = null) {
    this.classList.toggle('is-collapsed', collapsed);
    document.documentElement.classList.toggle(COLLAPSED_HTML_CLASS, collapsed);

    const btn = toggle || this.querySelector('[data-upsell-toggle]');
    if (btn instanceof HTMLElement) {
      btn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
      btn.setAttribute('aria-label', collapsed ? 'Teklifleri göster' : 'Teklifleri gizle');
    }

    try {
      sessionStorage.setItem(COLLAPSED_STORAGE_KEY, collapsed ? '1' : '0');
    } catch (e) {
      /* private mode */
    }
  }

  #restoreCollapsedState() {
    let collapsed = document.documentElement.classList.contains(COLLAPSED_HTML_CLASS);
    if (!collapsed) {
      try {
        collapsed = sessionStorage.getItem(COLLAPSED_STORAGE_KEY) === '1';
      } catch (e) {
        collapsed = false;
      }
    }
    this.#setCollapsed(collapsed);
  }

  /**
   * @param {HTMLElement} button
   */
  async #addVariant(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || this.#inflight.has(button)) return;

    this.#inflight.add(button);
    button.setAttribute('disabled', 'true');
    if ('disabled' in button) /** @type {any} */ (button).disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean)
      .join(',');

    const deferred = CartLinesUpdateEvent.createPromise();

    try {
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variantId), quantity: 1 }],
          promise: deferred.promise,
        })
      );

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          ...(sectionIds ? { sections: sectionIds } : {}),
        }),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Add to cart failed');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
      if (!cartResponse.ok) throw new Error('Cart refresh failed');
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          sections: data.sections,
          source: 'cart-drawer-upsell',
          didError: false,
        },
      });

      if (button.isConnected) {
        button.classList.remove('is-loading');
        button.classList.add('is-added');
        button.textContent = 'Eklendi';
        button.removeAttribute('aria-busy');
      }
    } catch (error) {
      deferred.reject(error instanceof Error ? error : new Error(String(error)));
      this.#resetButton(button);
      console.error('[cart-drawer-upsell]', error);
    } finally {
      this.#inflight.delete(button);
    }
  }

  /**
   * @param {HTMLElement} button
   */
  #resetButton(button) {
    if (!button.isConnected) return;
    button.removeAttribute('disabled');
    if ('disabled' in button) /** @type {any} */ (button).disabled = false;
    button.removeAttribute('aria-busy');
    button.classList.remove('is-loading');
  }
}

if (!customElements.get('cart-drawer-upsell')) {
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}
